import { useEffect } from 'react';
import { getViewportStableHeight, onViewportChanged } from '@astral/telegram/webapp';

/**
 * Publishes `--astral-stage-h`: a viewport height that stays put when the
 * software keyboard opens.
 *
 * The celestial background is a set of `position: fixed` layers sized with
 * percentage insets, so their box is derived from the viewport. Telegram's iOS
 * WebView SHRINKS the webview while the keyboard is up, which recomputed those
 * percentages and visibly compressed the gradient field, the halftone and the
 * particle stage. Sizing the background from this variable instead decouples it
 * from that transient shrinkage.
 *
 * Source of truth, in order:
 *  1. Telegram's `viewportStableHeight` — defined as excluding the keyboard.
 *  2. `window.innerHeight`, but only ever allowed to GROW, or to change when the
 *     width changes (a genuine rotation / window resize). A height-only
 *     shrink is exactly what a keyboard looks like, so it is ignored.
 *
 * The foreground is untouched: screens, sheets and inputs keep resizing and
 * scrolling normally so focused fields stay reachable.
 */
export function useStableViewport(): void {
  useEffect(() => {
    const root = document.documentElement;
    let stableHeight = 0;
    let lastWidth = window.innerWidth;

    const apply = (height: number): void => {
      if (height <= 0 || height === stableHeight) return;
      stableHeight = height;
      root.style.setProperty('--astral-stage-h', `${height}px`);
    };

    const measure = (): void => {
      const widthChanged = window.innerWidth !== lastWidth;
      if (widthChanged) {
        // A rotation or real resize: re-baseline in both directions.
        lastWidth = window.innerWidth;
        stableHeight = 0;
      }
      const telegram = getViewportStableHeight();
      if (telegram !== null) {
        apply(telegram);
        return;
      }
      // Fallback: never shrink on a height-only change (that is the keyboard).
      const height = window.innerHeight;
      if (height > stableHeight) apply(height);
    };

    measure();

    // `viewportChanged` also fires while the keyboard animates; `measure` is
    // idempotent and ignores downward height-only moves, so this cannot loop.
    const offTelegram = onViewportChanged(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      offTelegram();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);
}
